import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLazyGetVehicleWarrantyInformationQuery } from '../../../../../../../api/endpoints/vehicleWarrantyInformationReport';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';

const VehicleWarrantyInfoDialog = ({ visible, onHide, chassisNo }) => {
    const { th } = useLanguage();
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const [fetchWarrantyInfo, { data, isFetching }] = useLazyGetVehicleWarrantyInformationQuery();

    useEffect(() => {
        if (visible && chassisNo) {
            fetchWarrantyInfo({
                sasiNo: chassisNo,
                pageNumber: 1,
                pageSize: 1000
            });
        }
    }, [visible, chassisNo, fetchWarrantyInfo]);

    const items = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data]);

    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return items;

        const isDateKey = (key) => /date|tarih|baslangic|bitis/i.test(key);

        return [...items].sort((a, b) => {
            let aVal = a?.[sortField];
            let bVal = b?.[sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return sortOrder;
            if (bVal == null) return -sortOrder;

            const looksLikeDate = isDateKey(sortField) || (typeof aVal === 'string' && /^\d{2}\.\d{2}\.\d{4}/.test(aVal)) || aVal instanceof Date || bVal instanceof Date;

            if (looksLikeDate) {
                const parseDate = (val) => {
                    if (val instanceof Date) return val;
                    if (typeof val === 'string' && /^\d{2}\.\d{2}\.\d{4}/.test(val)) {
                        const [day, month, year] = val.split('.');
                        return new Date(year, month - 1, day);
                    }
                    return new Date(val);
                };
                return sortOrder * (parseDate(aVal).getTime() - parseDate(bVal).getTime());
            }

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortOrder * (aVal - bVal);
            }

            return sortOrder * String(aVal).localeCompare(String(bVal), 'tr', { numeric: true, sensitivity: 'base' });
        });
    }, [items, sortField, sortOrder]);

    const handleSort = useCallback((e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    }, []);

    const columns = useMemo(
        () => [
            { field: 'zNumber', header: th('vehicleWarrantyInformation.columns.ZNumber') },
            { field: 'sasiNo', header: th('vehicleWarrantyInformation.columns.SasiNo') },
            { field: 'garantiTipi', header: th('vehicleWarrantyInformation.columns.GarantiTipi') },
            { field: 'strWarranty', header: th('vehicleWarrantyInformation.columns.StrWarranty') },
            { field: 'extWarranty', header: th('vehicleWarrantyInformation.columns.ExtWarranty') },
            { field: 'zMonth', header: th('vehicleWarrantyInformation.columns.ZMonth') },
            { field: 'mileage', header: th('vehicleWarrantyInformation.columns.Mileage'), body: (row) => (row.mileage != null ? new Intl.NumberFormat('tr-TR').format(row.mileage) : '') }
        ],
        [th]
    );

    const header = (
        <div className="flex align-items-center gap-2">
            <i className="pi pi-shield" style={{ color: '#0B3187' }}></i>
            <span>{th('vehicleWarrantyInformation.title')}</span>
            {chassisNo && <span className="text-500 font-normal text-sm ml-2">— {chassisNo}</span>}
        </div>
    );

    return (
        <Dialog header={header} visible={visible} onHide={onHide} style={{ width: '90vw', maxWidth: '1400px' }} modal dismissableMask resizable={false}>
            {isFetching ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                </div>
            ) : (
                <DataTable
                    value={sortedData}
                    dataKey="zNumber"
                    showGridlines
                    className="p-datatable-gridlines"
                    onSort={handleSort}
                    sortField={sortField || undefined}
                    sortOrder={sortOrder || 0}
                    sortMode="single"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    emptyMessage={th('common.noRecords')}
                    tableStyle={{ minWidth: '60rem' }}
                >
                    {columns.map((col) => (
                        <Column key={col.field} field={col.field} header={col.header} sortable body={col.body || undefined} />
                    ))}
                </DataTable>
            )}
        </Dialog>
    );
};

export default VehicleWarrantyInfoDialog;
